import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck,
  Search,
  Save,
  Loader2,
  ChevronDown,
  Eye,
  Plus,
  Pencil,
  Trash2,
  Download,
  CheckSquare,
  X,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useInstitution } from '../../contexts/InstitutionContext';
import { Card, Input, Button, Badge, Modal } from '../../Components/ui';
import { useToast, useConfirm } from '../../Components/Feedback';
import { API_URL } from '../../api/config';

// Fixos no código — sempre existem, nunca podem ser apagados. Além destes, o
// master pode criar perfis novos e genéricos pela própria tela (ver
// backend/perfis-customizados.js) — esses vêm de /api/perfis-customizados e
// entram na lista de abas dinamicamente (ver perfisDisponiveis abaixo).
const PERFIS_FIXOS = [
  { value: 'monitor', label: 'Monitor' },
  { value: 'professor', label: 'Educador' },
  { value: 'coordenador', label: 'Coordenador' },
];

// Fallback caso a chamada a /api/permissoes/recursos ainda não tenha voltado —
// mesmo catálogo fixo de backend/telas.js, só pra não piscar a tela vazia.
const RECURSOS_PADRAO = [
  { recurso: 'visualizar', label: 'Visualizar' },
  { recurso: 'criar', label: 'Criar' },
  { recurso: 'editar', label: 'Editar' },
  { recurso: 'excluir', label: 'Excluir' },
  { recurso: 'exportar', label: 'Exportar' },
];

const ICONE_RECURSO = {
  visualizar: Eye,
  criar: Plus,
  editar: Pencil,
  excluir: Trash2,
  exportar: Download,
};

const LABEL_RECURSO_POR_TELA = {
  '/notificacoes': {
    criar: 'Enviar Notificação Pontual',
    visualizar: 'Visualizar Notificações',
  },
};

const normalizar = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Tela de Permissões — master escolhe um perfil (monitor/educador/coordenador
// — master fica de fora, tem acesso total sempre, fixo no backend) e marca
// quais telas esse perfil acessa e, dentro de cada tela marcada, quais ações
// (visualizar/criar/editar/excluir/exportar) ele pode fazer. As telas
// decidem o menu lateral e o bloqueio de rota (ver PerfilRoute em App.js).
//
// Tudo aqui é POR INSTITUIÇÃO (ver backend/migrate-permissoes-por-instituicao.js):
// "monitor" pode ter um conjunto de telas na instituição A e outro na
// instituição B. A tela sempre edita a instituição ATIVA (a mesma trocada
// pelo "Trocar Unidade" no menu, ver InstitutionContext.js) — por isso não
// tem um seletor de instituição próprio aqui, só mostra qual está em edição.
function Permissoes() {
  const { token, user } = useAuth();
  const { institutionId, institutionName } = useInstitution();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const headers = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-institution-id': institutionId,
    }),
    [token, institutionId],
  );

  const { data: perfisCustomizados = [] } = useQuery({
    queryKey: ['perfis-customizados', institutionId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/perfis-customizados`, { headers });
      if (!res.ok) throw new Error('Erro ao carregar perfis customizados.');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!token && !!institutionId,
  });

  const perfisDisponiveis = useMemo(() => {
    let base = PERFIS_FIXOS;
    if (user?.perfil === 'coordenador') {
      base = base.filter((p) => p.value !== 'coordenador');
    }
    return [
      ...base,
      ...perfisCustomizados.map((p) => ({ value: p.chave, label: p.nome, customizado: true })),
    ];
  }, [perfisCustomizados, user?.perfil]);

  const [perfilSelecionado, setPerfilSelecionado] = useState(() =>
    user?.perfil === 'coordenador' ? 'monitor' : 'coordenador',
  );

  useEffect(() => {
    if (user?.perfil === 'coordenador' && perfilSelecionado === 'coordenador') {
      setPerfilSelecionado(perfisDisponiveis[0]?.value || 'monitor');
    }
  }, [user?.perfil, perfilSelecionado, perfisDisponiveis]);

  const { data: telas = [], isLoading: carregandoTelas } = useQuery({
    queryKey: ['permissoes-telas'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/permissoes/telas`, { headers });
      if (!res.ok) throw new Error('Erro ao carregar lista de telas.');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!token,
    staleTime: 60 * 1000,
  });

  const { data: recursosCatalogo = RECURSOS_PADRAO } = useQuery({
    queryKey: ['permissoes-recursos'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/permissoes/recursos`, { headers });
      if (!res.ok) throw new Error('Erro ao carregar lista de ações.');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!token,
    staleTime: Infinity,
  });

  const { data: permissoesDoPerfil, isLoading: carregandoPerfil } = useQuery({
    queryKey: ['permissoes', perfilSelecionado, institutionId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/permissoes/${perfilSelecionado}`, { headers });
      if (!res.ok) throw new Error('Erro ao carregar permissões do perfil.');
      const body = await res.json();
      return body?.data ?? body;
    },
    enabled: !!token && !!perfilSelecionado && !!institutionId,
    refetchOnMount: 'always',
  });

  const [telasMarcadas, setTelasMarcadas] = useState(new Set());
  const [recursosMarcados, setRecursosMarcados] = useState(new Set()); // chaves "tela|recurso"
  const [busca, setBusca] = useState('');
  const [expandidas, setExpandidas] = useState(new Set());
  const [salvando, setSalvando] = useState(false);

  // --- Criar / apagar perfil customizado ---
  const [modalNovoPerfilAberto, setModalNovoPerfilAberto] = useState(false);
  const [nomeNovoPerfil, setNomeNovoPerfil] = useState('');
  const [criandoPerfil, setCriandoPerfil] = useState(false);

  const criarPerfil = async (e) => {
    e.preventDefault();
    const nome = nomeNovoPerfil.trim();
    if (!nome) return;
    setCriandoPerfil(true);
    try {
      const res = await fetch(`${API_URL}/api/perfis-customizados`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ nome }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao criar perfil.');
      toast.success(data?.message || `Perfil "${nome}" criado.`);
      setModalNovoPerfilAberto(false);
      setNomeNovoPerfil('');
      await queryClient.invalidateQueries({ queryKey: ['perfis-customizados'] });
      const chave = data?.data?.chave ?? data?.chave;
      setPerfilSelecionado(chave);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCriandoPerfil(false);
    }
  };

  const apagarPerfil = async (perfil) => {
    if (
      !(await confirm(
        `Apagar o perfil "${perfil.label}"? Só é possível se nenhum usuário tiver esse perfil no momento.`,
        { title: 'Apagar perfil', confirmText: 'Apagar' },
      ))
    )
      return;
    try {
      const res = await fetch(`${API_URL}/api/perfis-customizados/${perfil.value}`, {
        method: 'DELETE',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao apagar perfil.');
      toast.success(data?.message || 'Perfil apagado.');
      await queryClient.invalidateQueries({ queryKey: ['perfis-customizados'] });
      if (perfilSelecionado === perfil.value) setPerfilSelecionado('monitor');
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    if (!permissoesDoPerfil) return;
    setTelasMarcadas(new Set(permissoesDoPerfil.telas || []));
    const chaves = [];
    for (const [tela, lista] of Object.entries(permissoesDoPerfil.recursos || {})) {
      for (const recurso of lista) chaves.push(`${tela}|${recurso}`);
    }
    setRecursosMarcados(new Set(chaves));
    setExpandidas(new Set());
  }, [permissoesDoPerfil]);

  const buscaNorm = normalizar(busca.trim());
  const recursoCorrespondente = buscaNorm
    ? recursosCatalogo.find((r) => normalizar(r.label).includes(buscaNorm))
    : null;
  const telasFiltradas = !buscaNorm
    ? telas
    : telas.filter(
        (t) =>
          normalizar(t.label).includes(buscaNorm) ||
          normalizar(t.tela).includes(buscaNorm) ||
          !!recursoCorrespondente ||
          Object.values(LABEL_RECURSO_POR_TELA[t.tela] || {}).some((lbl) =>
            normalizar(lbl).includes(buscaNorm),
          ),
      );
  const buscaAtiva = !!buscaNorm;

  const alternarTela = (tela) => {
    setTelasMarcadas((prev) => {
      const novo = new Set(prev);
      if (novo.has(tela)) {
        novo.delete(tela);
        setRecursosMarcados((prevR) => {
          const novoR = new Set(prevR);
          for (const chave of [...novoR]) if (chave.startsWith(`${tela}|`)) novoR.delete(chave);
          return novoR;
        });
      } else {
        novo.add(tela);
        // Tela recém-liberada começa com todas as ações liberadas (mesmo
        // padrão "liberado por padrão" do backend) — evita mostrar 0/5 ações
        // numa tela que o master acabou de marcar.
        setRecursosMarcados((prevR) => {
          const novoR = new Set(prevR);
          recursosCatalogo.forEach((r) => novoR.add(`${tela}|${r.recurso}`));
          return novoR;
        });
      }
      return novo;
    });
  };

  const alternarRecurso = (tela, recurso) => {
    const chave = `${tela}|${recurso}`;
    setRecursosMarcados((prev) => {
      const novo = new Set(prev);
      if (novo.has(chave)) novo.delete(chave);
      else novo.add(chave);
      return novo;
    });
  };

  const alternarExpansao = (tela) => {
    setExpandidas((prev) => {
      const novo = new Set(prev);
      if (novo.has(tela)) novo.delete(tela);
      else novo.add(tela);
      return novo;
    });
  };

  const marcarVisiveis = () => {
    setTelasMarcadas((prev) => {
      const novo = new Set(prev);
      telasFiltradas.forEach((t) => novo.add(t.tela));
      return novo;
    });
  };

  const limparVisiveis = () => {
    const telasParaLimpar = new Set(telasFiltradas.map((t) => t.tela));
    setTelasMarcadas((prev) => {
      const novo = new Set(prev);
      telasParaLimpar.forEach((t) => novo.delete(t));
      return novo;
    });
    setRecursosMarcados((prev) => {
      const novo = new Set(prev);
      for (const chave of [...novo])
        if (telasParaLimpar.has(chave.split('|')[0])) novo.delete(chave);
      return novo;
    });
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const recursosPorTela = {};
      for (const chave of recursosMarcados) {
        const [tela, recurso] = chave.split('|');
        if (!recursosPorTela[tela]) recursosPorTela[tela] = [];
        recursosPorTela[tela].push(recurso);
      }
      const res = await fetch(`${API_URL}/api/permissoes/${perfilSelecionado}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ telas: [...telasMarcadas], recursos: recursosPorTela }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar permissões.');
      toast.success(
        data?.message ||
          'Permissões salvas. Quem já está logado só vê o efeito ao recarregar a página ou logar de novo.',
        6000,
      );
      queryClient.invalidateQueries({ queryKey: ['permissoes', perfilSelecionado] });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  const carregando = carregandoTelas || carregandoPerfil;

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-5xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm shrink-0">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Permissões</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Quais telas cada perfil acessa e o que pode fazer dentro delas. Master sempre tem
                acesso total.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              tone="info"
              title="Cada instituição tem sua própria configuração — troque de unidade no menu para editar outra."
            >
              Editando: {institutionName || '...'}
            </Badge>
            <Button
              variant="primary"
              icon={Save}
              disabled={carregando || salvando}
              onClick={salvar}
            >
              {salvando ? 'Salvando...' : 'Salvar alterações'}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex p-1 rounded-2xl bg-gray-100 dark:bg-gray-900 gap-1 flex-wrap">
            {perfisDisponiveis.map((p) => (
              <div key={p.value} className="relative group/tab">
                <button
                  type="button"
                  onClick={() => setPerfilSelecionado(p.value)}
                  className={`px-4 h-9 rounded-xl text-sm font-medium transition-all ${p.customizado ? 'pr-7' : ''} ${
                    perfilSelecionado === p.value
                      ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  {p.label}
                </button>
                {p.customizado && (
                  <button
                    type="button"
                    title="Apagar perfil"
                    onClick={(e) => {
                      e.stopPropagation();
                      apagarPerfil(p);
                    }}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 opacity-0 group-hover/tab:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setModalNovoPerfilAberto(true)}
            title="Criar novo perfil"
            className="w-9 h-9 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 flex items-center justify-center transition-colors shrink-0"
          >
            <UserPlus className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="w-full sm:w-96">
            <Input
              placeholder="Buscar tela ou ação (ex.: notas, excluir...)"
              icon={Search}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge tone="info">
              {telasMarcadas.size} de {telas.length} telas
            </Badge>
            <Badge tone="success">{recursosMarcados.size} ações liberadas</Badge>
            <button
              type="button"
              onClick={marcarVisiveis}
              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              Marcar visíveis
            </button>
            <button
              type="button"
              onClick={limparVisiveis}
              className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:underline"
            >
              Limpar visíveis
            </button>
          </div>
        </div>

        {carregando ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : telasFiltradas.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Nenhuma tela ou ação encontrada para "{busca}".</p>
          </div>
        ) : (
          <div className="space-y-2">
            {telasFiltradas.map((t) => {
              const ligada = telasMarcadas.has(t.tela);
              const expandida = buscaAtiva || expandidas.has(t.tela);
              const qtdRecursos = recursosCatalogo.filter((r) =>
                recursosMarcados.has(`${t.tela}|${r.recurso}`),
              ).length;

              return (
                <Card
                  key={t.tela}
                  padding="none"
                  className={`overflow-hidden transition-colors ${ligada ? 'border-blue-100 dark:border-blue-900/40' : ''}`}
                >
                  <div
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
                    onClick={() => alternarExpansao(t.tela)}
                  >
                    <input
                      type="checkbox"
                      checked={ligada}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => alternarTela(t.tela)}
                      className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium truncate ${ligada ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}`}
                      >
                        {t.label}
                      </p>
                      <p className="text-[11px] text-gray-400 dark:text-gray-600 font-mono truncate">
                        {t.tela}
                      </p>
                    </div>
                    {ligada && qtdRecursos > 0 && (
                      <Badge tone="neutral" className="hidden sm:inline-flex shrink-0">
                        {qtdRecursos}/{recursosCatalogo.length} ações
                      </Badge>
                    )}
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ${expandida ? 'rotate-180' : ''}`}
                    />
                  </div>

                  {expandida && (
                    <div
                      className={`px-4 pb-3 pt-1 flex flex-wrap gap-2 border-t border-gray-50 dark:border-gray-800 ${ligada ? '' : 'opacity-50'}`}
                    >
                      {recursosCatalogo.map((r) => {
                        const Icone = ICONE_RECURSO[r.recurso] || CheckSquare;
                        const marcado = recursosMarcados.has(`${t.tela}|${r.recurso}`);
                        const destacar = recursoCorrespondente?.recurso === r.recurso;
                        const labelAcao = LABEL_RECURSO_POR_TELA[t.tela]?.[r.recurso] || r.label;
                        return (
                          <button
                            key={r.recurso}
                            type="button"
                            disabled={!ligada}
                            onClick={() => alternarRecurso(t.tela, r.recurso)}
                            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-medium border transition-all disabled:cursor-not-allowed ${
                              marcado
                                ? 'bg-blue-600 border-blue-600 text-white'
                                : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-blue-300'
                            } ${destacar ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                          >
                            <Icone className="w-3.5 h-3.5" />
                            {labelAcao}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <Modal
        isOpen={modalNovoPerfilAberto}
        onClose={() => setModalNovoPerfilAberto(false)}
        title="Novo perfil"
        maxWidth="sm"
      >
        <form onSubmit={criarPerfil} className="space-y-4">
          <Input
            label="Nome do perfil"
            value={nomeNovoPerfil}
            onChange={(e) => setNomeNovoPerfil(e.target.value)}
            placeholder="Ex.: Secretaria"
            required
            autoFocus
          />
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Criado só para "{institutionName}" — outras instituições não o veem. Começa sem nenhuma
            tela liberada; marque o que ele pode acessar depois de criado. Não herda nada de
            Educador nem Coordenador.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setModalNovoPerfilAberto(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={criandoPerfil}>
              {criandoPerfil ? 'Criando...' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Permissoes;
